import { useState } from 'react'
import { AlertTriangle, CalendarCheck, CalendarClock, Check, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCopilotStore } from '../../stores/copilotStore'
import { useApproveCallback } from '../../api/telemetry'

interface Props {
  callSid: string
}

/** Live notebook card that surfaces when the copilot analyzer emits
 *  COPILOT_CALLBACK_PROPOSED mid-call. The rep can confirm / edit / dismiss
 *  without leaving the call. Approval goes through the same server-side
 *  endpoint the post-call PendingProposalsPanel uses. */
export function LiveCallbackCard({ callSid }: Props) {
  const proposal = useCopilotStore((s) => s.byCallSid[callSid]?.callback ?? null)
  const dismissCallback = useCopilotStore((s) => s.dismissCallback)
  const markApproved = useCopilotStore((s) => s.markCallbackApproved)
  const { t } = useTranslation('calls')
  const mutation = useApproveCallback(callSid)
  const [whenLocal, setWhenLocal] = useState(toLocalInput(proposal?.callback_at ?? ''))
  const [reason, setReason] = useState(proposal?.callback_reason ?? '')
  const [editing, setEditing] = useState(false)

  if (!proposal || proposal.dismissed) return null

  if (proposal.approved) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-success/5 px-3 py-2 text-sm text-text-secondary">
        <CalendarCheck className="h-4 w-4 text-success" />
        <span>{t('detail.proposals.callback.savedSummary', { when: formatWhen(proposal.callback_at) })}</span>
      </div>
    )
  }

  return (
    <section className="rounded-lg border border-primary/40 bg-primary/5 p-4">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold text-text-primary">
          {t('detail.proposals.callback.title')}
        </h3>
      </div>
      {proposal.needs_review && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{t('detail.proposals.callback.needsReview')}</span>
        </div>
      )}
      {!editing ? (
        <p className="mt-2 text-sm text-text-primary">
          {t('detail.proposals.callback.title')}: {formatWhen(proposal.callback_at)}
          {proposal.callback_reason && (
            <span className="text-text-secondary"> — {proposal.callback_reason}</span>
          )}
        </p>
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-2">
          <label className="text-xs font-medium text-text-secondary">
            {t('detail.proposals.callback.when')}
            <input
              type="datetime-local"
              value={whenLocal}
              onChange={(e) => setWhenLocal(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary"
            />
          </label>
          <label className="text-xs font-medium text-text-secondary">
            {t('detail.proposals.callback.reason')}
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary"
            />
          </label>
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() =>
            mutation.mutate(
              {
                action: 'approve',
                callback_at: editing
                  ? localInputToIso(whenLocal)
                  : proposal.callback_at,
                callback_reason: editing ? reason : proposal.callback_reason,
              },
              { onSuccess: () => markApproved(callSid) },
            )
          }
          className="inline-flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-60"
        >
          <Check className="h-3.5 w-3.5" />
          {t('copilot.callback.confirm')}
        </button>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="rounded border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-elevated"
          >
            {t('copilot.callback.edit')}
          </button>
        )}
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => {
            mutation.mutate({ action: 'dismiss' })
            dismissCallback(callSid)
          }}
          className="ml-auto inline-flex items-center gap-1.5 rounded border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-elevated"
        >
          <X className="h-3.5 w-3.5" />
          {t('copilot.callback.dismiss')}
        </button>
      </div>
    </section>
  )
}

function toLocalInput(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => n.toString().padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

function localInputToIso(local: string): string {
  if (!local) return ''
  const d = new Date(local)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString()
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
